'use client';
import { useRef } from 'react';
import NextImage from 'next/image';
import { motion, useInView } from 'motion/react';
import { cn } from 'lib/utils';
import { type ImageField } from 'lib/types';
import { type BloomProps, Bloom, RichText, Wrapper } from 'ui';

export type TextImageBleedProps = {
  className?: string;
  title: string;
  content: {
    title: string;
    description: string;
  }[];
  image: ImageField;
  alignment?: 'left' | 'right';
  bloom?: BloomProps;
};

const TextImageBleed = ({
  className,
  alignment = 'left',
  title,
  content,
  image,
  bloom,
}: TextImageBleedProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-20%' });

  return (
    <motion.section {...{ ref }}>
      <Wrapper
        variant="page-full"
        className={cn(
          'relative flex flex-col-reverse gap-12 lg:gap-20 xl:gap-30 lg:items-center !p-0',
          {
            'lg:flex-row lg:!pl-wrapper-page-full-x': alignment === 'left',
            'lg:flex-row-reverse lg:!pr-wrapper-page-full-x':
              alignment === 'right',
          },
          className
        )}
      >
        <Bloom {...bloom} />

        {/* Content */}
        <div className="lg:grow lg:shrink lg:basis-3/7 flex flex-col gap-10 max-lg:px-wrapper-page-full-x relative z-2">
          <motion.h2
            className="text-h3"
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
            transition={{
              duration: 0.6,
              delay: 0.1,
              ease: [0.25, 0.1, 0.25, 1],
            }}
          >
            {title}
          </motion.h2>

          {content.map((block, index) => (
            <motion.article
              key={index}
              className="flex flex-col gap-4"
              initial={{ opacity: 0, x: -40 }}
              animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
              transition={{
                duration: 0.6,
                delay: 0.2 + index * 0.15,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              <h3 className="text-p1">{block.title}</h3>
              <RichText content={block.description} theme="neutral" />
            </motion.article>
          ))}
        </div>

        {/* Image */}
        <div className="lg:grow lg:shrink lg:basis-4/7 lg:max-w-180 relative z-2">
          <NextImage
            src={image.src}
            alt={image.alt}
            width={image.width}
            height={image.height}
            className={cn(
              'w-full h-auto object-contain wrapper-max:rounded-lg',
              {
                'lg:rounded-l-lg': alignment === 'left',
                'lg:rounded-r-lg': alignment === 'right',
              }
            )}
            role={!image.alt ? 'presentation' : undefined}
          />
        </div>
      </Wrapper>
    </motion.section>
  );
};

export default TextImageBleed;
